import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { chatApi } from '../api/client'
import type { AddGroupMembersRequest, ChatConversation, CreateGroupRequest } from '../api/types'
import { chatKeys } from './keys'
import { useChatAccountId } from './queries'
import { useChatUiStore } from './chatUiStore'

export function useMarkRead() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ conversationId, lastReadMessageId }: { conversationId: number; lastReadMessageId: number }) =>
      chatApi.markRead(conversationId, lastReadMessageId),
    onMutate: ({ conversationId, lastReadMessageId }) => {
      if (accountId === null) return
      queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (rows) =>
        rows?.map((row) =>
          row.id === conversationId
            ? { ...row, hasUnread: false, myLastReadMessageId: lastReadMessageId }
            : row,
        ),
      )
    },
  })
}

export function useSetMuted() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ conversationId, muted }: { conversationId: number; muted: boolean }) =>
      chatApi.setMuted(conversationId, muted),
    onSuccess: (_data, { conversationId, muted }) => {
      if (accountId === null) return
      queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (rows) =>
        rows?.map((row) => (row.id === conversationId ? { ...row, muted } : row)),
      )
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversation(accountId, conversationId) })
    },
  })
}

import { chatErrorMessage } from './chatErrors'
import { CHAT_COPY } from './chatCopy'
import { useChatOutboxStore, type OutboxMessage } from './chatOutboxStore'
import type { ChatMessage, SendMessageRequest } from '../api/types'

type MessagePages = { pages: ChatMessage[][]; pageParams: unknown[] }

export function appendMessageToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  accountId: number,
  conversationId: number,
  message: ChatMessage,
): void {
  queryClient.setQueryData<MessagePages>(chatKeys.messages(accountId, conversationId), (old) => {
    if (!old || old.pages.length === 0) return old
    const [first, ...rest] = old.pages
    if (first.some((existing) => existing.id === message.id)) {
      return { ...old, pages: [first.map((e) => (e.id === message.id ? message : e)), ...rest] }
    }
    return { ...old, pages: [[...first, message], ...rest] }
  })
}

export function useSendMessage() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()
  const addPending = useChatOutboxStore((state) => state.addPending)
  const markSent = useChatOutboxStore((state) => state.markSent)
  const markFailed = useChatOutboxStore((state) => state.markFailed)

  return useMutation({
    mutationFn: async ({
      conversationId,
      request,
    }: {
      conversationId: number
      request: SendMessageRequest
      optimistic: OutboxMessage
    }) => chatApi.send(conversationId, request),
    onMutate: ({ optimistic }) => {
      addPending(optimistic)
    },
    onSuccess: (message, { conversationId, request }) => {
      markSent(conversationId, request.clientRef)
      if (accountId === null) return
      appendMessageToCache(queryClient, accountId, conversationId, message)
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations(accountId) })
    },
    onError: (error, { conversationId, request }) => {
      markFailed(conversationId, request.clientRef, chatErrorMessage(error, CHAT_COPY.failed))
    },
  })
}

export function useRetrySend() {
  const send = useSendMessage()
  const markRetrying = useChatOutboxStore((state) => state.markRetrying)

  return (message: OutboxMessage) => {
    markRetrying(message.conversationId, message.clientRef)
    send.mutate({
      conversationId: message.conversationId,
      request: {
        clientRef: message.clientRef,
        body: message.body,
        attachmentIds: message.attachmentIds,
        mentions: message.mentions,
        replyToMessageId: message.replyToMessageId,
      },
      optimistic: { ...message, status: 'pending', error: null },
    })
  }
}

export function useDeleteMessage() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ messageId }: { conversationId: number; messageId: number }) =>
      chatApi.deleteMessage(messageId),
    onSuccess: (_data, { conversationId, messageId }) => {
      if (accountId === null) return
      queryClient.setQueryData<MessagePages>(chatKeys.messages(accountId, conversationId), (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.map((message) =>
              message.id === messageId ? { ...message, isDeleted: true, content: null } : message,
            ),
          ),
        }
      })
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversations(accountId) })
    },
  })
}

export function useUploadAttachment() {
  return useMutation({
    mutationFn: ({
      conversationId,
      file,
      contentType,
      dimensions,
    }: {
      conversationId: number
      file: File
      contentType: string
      dimensions: { width?: number; height?: number }
    }) => chatApi.upload(conversationId, file, contentType, dimensions),
  })
}

export function upsertConversation(
  queryClient: QueryClient,
  accountId: number,
  conversation: ChatConversation,
): void {
  queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (existing) => {
    if (!existing) return [conversation]
    const index = existing.findIndex((row) => row.id === conversation.id)
    if (index === -1) return [conversation, ...existing]
    const next = [...existing]
    next[index] = conversation
    return next
  })
}

export function useStartClientConversation() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ clientId }: { clientId: number }) => chatApi.startClientConversation(clientId),
    onSuccess: (conversation) => {
      if (accountId === null) return
      upsertConversation(queryClient, accountId, conversation)
    },
  })
}

export function useCreateDirect() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ userId }: { userId: number }) => chatApi.createDirect(userId),
    onSuccess: (conversation) => {
      if (accountId === null) return
      upsertConversation(queryClient, accountId, conversation)
    },
  })
}

export function useCreateGroup() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: (request: CreateGroupRequest) => chatApi.createGroup(request),
    onSuccess: (conversation) => {
      if (accountId === null) return
      upsertConversation(queryClient, accountId, conversation)
    },
  })
}

export function useAddGroupMembers() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: AddGroupMembersRequest }) =>
      chatApi.addGroupMembers(id, request),
    onSuccess: (_data, { id }) => {
      if (accountId === null) return
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversation(accountId, id) })
    },
  })
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) => chatApi.removeGroupMember(id, userId),
    onSuccess: (_data, { id }) => {
      if (accountId === null) return
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversation(accountId, id) })
    },
  })
}

export function useRenameGroup() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) => chatApi.renameGroup(id, title),
    onSuccess: (_data, { id, title }) => {
      if (accountId === null) return
      queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (rows) =>
        rows?.map((row) => (row.id === id ? { ...row, title } : row)),
      )
      void queryClient.invalidateQueries({ queryKey: chatKeys.conversation(accountId, id) })
    },
  })
}

export function useLeaveGroup() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ id }: { id: number }) => chatApi.leaveGroup(id),
    onSuccess: (_data, { id }) => {
      if (accountId === null) return
      queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (rows) =>
        rows?.filter((row) => row.id !== id),
      )
      queryClient.removeQueries({ queryKey: chatKeys.conversation(accountId, id) })
      queryClient.removeQueries({ queryKey: chatKeys.messages(accountId, id) })
      useChatUiStore.getState().setSelectedConversation(null)
    },
  })
}

export function useSendShiftBroadcast() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ shiftId, body, guardUserIds }: { shiftId: number; body: string; guardUserIds: number[] | null }) =>
      chatApi.sendShiftBroadcast(shiftId, body, guardUserIds),
    onSuccess: () => {
      if (accountId === null) return
      void queryClient.invalidateQueries({ queryKey: chatKeys.scope(accountId) })
    },
  })
}

export function useSendBroadcast() {
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()

  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: number; body: string }) =>
      chatApi.sendBroadcast(clientId, body),
    onSuccess: () => {
      if (accountId === null) return
      void queryClient.invalidateQueries({ queryKey: chatKeys.scope(accountId) })
    },
  })
}
